import path from "path";
import { Binary } from "./Binary";
import { Config as ConfigClass } from './Config';
import { spawn } from "child_process";
import { ProcessResponse, ResponseEngine } from "../@types/Engine";
import { BinaryType } from "../@types/Binary";
import { createRequire } from 'module';

class Engine {
    private name: string;
    private config: any;
    private arguments: any;
    private binary: BinaryType | null = null;
    private timeout: number;

    constructor(name: string, timeout = 30000) {
        this.name = name;
        this.config = this.setConfig(name);
        this.arguments = this.setArguments();
        this.timeout = timeout;
    }

    async initializeBinary(): Promise<void> {
        if (!this.binary) {
            this.binary = await Binary.get();
        }
    }

    setArguments() {
        let args = [];
        if (this.config.type == 'sqlite') {
            args = [
                '--id', 'dbcube-' + this.name,
                '--database-ref', this.name,
                '--database', this.config.config.DATABASE + ".db",
                '--motor', this.config.type
            ];
        } else {
            args = [
                '--id', 'dbcube-' + this.name,
                '--database-ref', this.name,
                '--database', this.config.config.DATABASE,
                '--host', this.config.config.HOST,
                '--port', this.config.config.PORT,
                '--user', this.config.config.USER,
                '--password', this.config.config.PASSWORD,
                '--motor', this.config.type
            ];
        }
        return args;
    }

    setConfig(name: string) {
        const configInstance = new ConfigClass();

        try {
            const configFilePath = path.resolve(process.cwd(), 'dbcube.config.js');
            // Use __filename for CJS, process.cwd() for ESM
            const requireUrl = typeof __filename !== 'undefined' ? __filename : process.cwd();
            const require = createRequire(requireUrl);
            // Clear require cache to ensure fresh load
            delete require.cache[require.resolve(configFilePath)];
            const configModule = require(configFilePath);
            const configFn = configModule.default || configModule;

            if (typeof configFn === 'function') {
                configFn(configInstance);
            } else {
                console.error('❌ El archivo dbcube.config.js no exporta una función.');
            }
        } catch (error: any) {
            console.error('❌ Error loading config file:', error.message);
            if (error.code === 'MODULE_NOT_FOUND') {
                console.error('❌ Config file not found, please create a dbcube.config.js file');
            }
        }

        return configInstance.getDatabase(name);

    }

    getConfig() {
        return this.config;
    }

    async run(binary: string, args: []) {
        await this.initializeBinary();

        if (!this.binary) {
            throw new Error('Binary not initialized');
        }

        return new Promise<ResponseEngine>((resolve, reject) => {
            const child = spawn(this.binary![binary as keyof BinaryType], [...this.arguments, ...args]);

            let stdoutBuffer = '';
            let stderrBuffer = '';
            let isResolved = false;

            const timeoutId = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    child.kill();
                    reject(new Error('Process timeout'));
                }
            }, this.timeout);

            const resolveOnce = (response: ResponseEngine) => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeoutId);
                    resolve(response);
                }
            };

            child.stdout.on('data', (data) => {
                stdoutBuffer += data.toString();
                // console.log(stdoutBuffer)

                // Buscar respuesta JSON completa
                const match = stdoutBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match) {
                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);
                        resolveOnce({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        resolveOnce({
                            status: 500,
                            message: 'Failed to parse response JSON',
                            data: null
                        });
                    }
                }
            });

            child.stderr.on('data', (data) => {
                stderrBuffer += data.toString();
                // console.log(stderrBuffer)

                // Buscar respuesta JSON completa
                const match = stderrBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match) {
                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);
                        resolveOnce({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        resolveOnce({
                            status: 500,
                            message: 'Failed to parse response JSON',
                            data: null
                        });
                    }
                }
            });


            child.on('close', (code) => {
                clearTimeout(timeoutId);
                if (!isResolved) {
                    resolveOnce({
                        status: code === 0 ? 200 : 500,
                        message: code === 0 ? 'Process completed' : `Process exited with code ${code}`,
                        data: null
                    });
                }
            });

            child.on('error', (error) => {
                clearTimeout(timeoutId);
                if (!isResolved) {
                    resolveOnce({
                        status: 500,
                        message: `Process error: ${error.message}`,
                        data: null
                    });
                }
            });

            child.unref();
        });
    }
}

export { Engine }